/**
 * ================================================================================================
 *  THE ASSISTANT'S CONVERSATION SURVIVES A SCREEN CHANGE — the Owner's requirement of 2026-10-28
 * ================================================================================================
 *
 *     *"העוזר עונה נכון ומפנה למסך המתאים, אבל לחיצה על הקישור או מעבר למסך אחר סוגרים אותו
 *     ומאבדים את השיחה. נדרש: לשמור את השאלה, התשובה והקישורים במעבר בין מסכים; להשאיר את החלונית
 *     פתוחה, אלא אם המשתמש סגר אותה; בסגירה ופתיחה מחדש, לחזור לאותה שיחה; לעדכן את הקשר המסך
 *     לשאלה הבאה, בלי למחוק את ההיסטוריה; לבודד את השיחה לפי המשתמש והארגון, ולא לחשוף אותה לאחר
 *     יציאה או החלפת חשבון."*
 *
 * ── THE CAUSE, DIAGNOSED IN THE SOURCE AND NOT GUESSED ─────────────────────────────────────────
 *
 * Every link in this product is a plain `<a href>` — a FULL DOCUMENT LOAD. `SupportAssistant` held
 * its open flag and its question in `useState`, so following one of the assistant's own links threw
 * both away along with the whole React tree. The panel did not "close"; it ceased to exist and was
 * rebuilt empty.
 *
 * And there was no conversation to lose in the first place: the component kept ONE submitted
 * question (`asked`). *"בלי למחוק את ההיסטוריה"* therefore means building a history, not preserving
 * one.
 *
 * ── WHY `sessionStorage` AND NOT THE SERVER ────────────────────────────────────────────────────
 *
 * The assistant answers out of a static guide that ships with the client. A question and the screen
 * it was asked from are not facts the office needs kept, they are not auditable acts, and sending
 * them to the server would create a record of what a person did not understand. `sessionStorage`
 * ends with the browser tab, which is the right lifetime for that.
 *
 * It is READ AND WRITTEN DEFENSIVELY. A private window, cleared site data and a storage quota all
 * make these calls throw, and a thrown assistant must not take a screen down: every access is
 * wrapped, and a failure degrades to the behaviour the product had yesterday.
 *
 * ── THE ISOLATION IS IN THE KEY, AND IT IS NOT A CONVENTION ────────────────────────────────────
 *
 * *"לבודד את השיחה לפי המשתמש והארגון, ולא לחשוף אותה לאחר יציאה או החלפת חשבון."*
 *
 * The stored record CARRIES the user uid and the tenant uid it was written under, and `load`
 * compares them against the session it is given. A mismatch returns NOTHING — it does not migrate
 * the conversation to the new identity and does not merge it. So a second person signing in on the
 * same machine, or the same person switching office, starts empty, and `forget()` is called on
 * sign-out so the text is not left behind at all.
 *
 * Reading somebody else's row would require their uid, which is exactly what the comparison refuses.
 */

/** One exchange: what was asked, and from where. The ANSWER is re-derived from the guide. */
export interface Exchange {
  readonly question: string;
  /**
   * The screen it was asked from, or `undefined`.
   *
   * Kept per exchange rather than once per conversation, because the Owner's fourth requirement is
   * that the screen context follows the NEXT question while the history stays as it was asked —
   * so an exchange remembers where it happened.
   */
  readonly screenId: string | undefined;
  /** Epoch milliseconds. For ordering only; never rendered as a time. */
  readonly atMs: number;
}

export interface Conversation {
  /** Did the reader leave the panel open? Restored exactly, which is requirement two. */
  readonly open: boolean;
  readonly exchanges: readonly Exchange[];
}

export const EMPTY_CONVERSATION: Conversation = { open: false, exchanges: [] };

/**
 * HOW MUCH IS KEPT, and why there is a limit at all.
 *
 * A conversation that grows without bound is a storage quota failure waiting for the one person who
 * never closes the tab, and the panel can only show so much before it is the wall this assistant
 * was built to replace. Twenty is far more than anybody scrolls back through and far less than a
 * quota.
 */
const KEEP = 20;

const KEY = 'glpoint.assistant.conversation';

interface Stored extends Conversation {
  readonly userUid: string | null;
  readonly tenantUid: string | null;
}

export interface Identity {
  readonly userUid: string | null;
  readonly tenantUid: string | null;
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // A private window, or site data blocked. The assistant works; it simply does not remember.
    return null;
  }
}

/**
 * THE CONVERSATION FOR THIS IDENTITY, or an empty one.
 *
 * Returns empty — never somebody else's — when the stored record was written by another user or
 * under another office, when nothing is stored, and when the store cannot be read or parsed.
 */
export function load(identity: Identity): Conversation {
  const store = storage();
  if (store === null) return EMPTY_CONVERSATION;

  let raw: string | null;
  try {
    raw = store.getItem(KEY);
  } catch {
    return EMPTY_CONVERSATION;
  }
  if (raw === null) return EMPTY_CONVERSATION;

  let parsed: Stored;
  try {
    parsed = JSON.parse(raw) as Stored;
  } catch {
    return EMPTY_CONVERSATION;
  }

  /*
   * THE COMPARISON IS THE ISOLATION. Both halves must match: the same person in a different office
   * must not see the questions they asked about the other one, because the answers were filtered by
   * the capabilities they held there.
   */
  if (parsed.userUid !== identity.userUid || parsed.tenantUid !== identity.tenantUid) {
    return EMPTY_CONVERSATION;
  }
  if (!Array.isArray(parsed.exchanges)) return EMPTY_CONVERSATION;

  return { open: parsed.open === true, exchanges: parsed.exchanges.slice(-KEEP) };
}

/** Write it under THIS identity. A failure is silent: the panel still works, it just forgets. */
export function save(identity: Identity, conversation: Conversation): void {
  const store = storage();
  if (store === null) return;
  const record: Stored = {
    userUid: identity.userUid,
    tenantUid: identity.tenantUid,
    open: conversation.open,
    exchanges: conversation.exchanges.slice(-KEEP),
  };
  try {
    store.setItem(KEY, JSON.stringify(record));
  } catch {
    // Quota, or a store that refuses writes. Nothing here is worth failing a screen over.
  }
}

/**
 * REMOVE IT. Called on sign-out, so the text is not merely unreachable but gone.
 *
 * The key comparison in `load` already refuses to show it to anybody else; this is the second half
 * — *"ולא לחשוף אותה לאחר יציאה"* — because "unreachable" and "not there" are different promises
 * and the Owner asked for the stronger one.
 */
export function forget(): void {
  const store = storage();
  if (store === null) return;
  try {
    store.removeItem(KEY);
  } catch {
    // Nothing further to do: the record was already unreadable to anybody but its own identity.
  }
}

/** Append a question, keeping the tail. The newest is last, which is reading order in the panel. */
export function remember(
  conversation: Conversation,
  exchange: Exchange,
): Conversation {
  return { open: conversation.open, exchanges: [...conversation.exchanges, exchange].slice(-KEEP) };
}
